import React, { useState, useEffect, useContext } from 'react'
import Icon from 'components/Icon'
import { Button } from '@arnsbogroup/react-voicearchive'
import useApi from 'hooks/useApi'
import messages from './messages'
import { FormattedMessage, useIntl } from 'react-intl'
import { setTheme, applyTheme } from 'themes/utils'
import useAudioPlayer from 'hooks/useAudioPlayer'
import { useKeyboardShortcut } from '@arnsbogroup/react-voicearchive/lib/hooks'
import SeekBar, { Timer } from 'components/AudioSeekBar'
import { Full as Loading } from 'components/Loading'
import { IntlContext } from 'App'

const PublicCustomerOrderPage = ({ match, location }) => {
  const { formatMessage } = useIntl()

  const { play, isPlaying, loading, load, currentTime, seek, duration } = useAudioPlayer({ preload: false })
  const [index, setIndex] = useState(null)
  const [copied, setCopied] = useState(false)
  const { dispatch } = useContext(IntlContext)

  const [props, loadingData, errors, refetch] = useApi(`/landingPage/index?hashId=${match.params.hashId}`)

  useEffect(() => {
    if (!props) {
      return
    }

    if (props.brand.theme) {
      setTheme(props.brand.theme)
      dispatch({ action: 'OVERWRITE', payload: props.brand.messages })
      document.title = formatMessage(messages.pageTitle, {
        partnerName: props.partnerName,
        customerName: props.brand.displayName,
      })
    }

    if (props.files.length) {
      load(props.files[0].url)
    }
  }, [props])

  useKeyboardShortcut(['ArrowRight'], () => {
    onPlayIndex(index + 1)
  })

  useKeyboardShortcut(['ArrowLeft'], () => {
    onPlayIndex(index - 1)
  })

  useKeyboardShortcut([' '], () => {
    play(!isPlaying)
  })

  if (loadingData || !props) {
    return <Loading />
  }

  const onPlayIndex = (index) => {
    let nextIndex = index

    if (!props || !props.files) {
      return
    }

    if (index < 0) {
      nextIndex = 0
    }

    if (index > (props.files.length - 1)) {
      nextIndex = props.files.length - 1
    }

    if (props.files[nextIndex]) {
      load(props.files[nextIndex].url)
      play(true)
      setIndex(nextIndex)
    }
  }

  const { files, link, asset_uri, brand, isLoggedIn } = props

  const onCopyToClipBoard = () => {
    navigator.clipboard.writeText(window.location.href)

    setCopied(true)
  }


  return (
    <div className="flex flex-col h-full w-full font-bold">
      <main className="grow md:flex justify-center items-center">
        <div className="w-full md:w-3/5">

          <div className="p-10 md:p-0 md:w-1/3 flex items-center mb-12">
            <img className="w-2/5" src={brand.logo_dark} />
            { asset_uri &&
              <React.Fragment>
                  <div className="w-1/5 text-center text-tertiary">&#10005;</div>
                  <img className="w-2/5 m-auto" src={asset_uri} />
              </React.Fragment>
            }
          </div>

          <div className="md:flex justify-between items-center p-12 md:p-0">
            <div className="w-full md:w-4/12">
              { files.length ? (
                <React.Fragment>
                  <h3 className="text-4xl">
                    <FormattedMessage {...messages.title} />
                  </h3>
                  <p className="my-3 leading-loose text-xl">
                    <FormattedMessage {...messages.subtitle} />
                  </p>

                  <p className="mt-10">
                    <div className="link cursor-pointer" onClick={onCopyToClipBoard}>{ window.location.href }</div>
                    { copied ? (
                      <small className="text-xs text-gray-400 uppercase">
                        <FormattedMessage {...messages.copied} />
                      </small>
                    ) : (
                      <small className="text-xs text-gray-400 uppercase">
                        <FormattedMessage {...messages.copy} />
                      </small>
                    ) }
                  </p>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <h3 className="text-4xl">
                    <FormattedMessage {...messages.titleInactive} />
                  </h3>
                  <p className="my-3 leading-loose text-xl">
                    <FormattedMessage {...messages.subtitleInactive} />
                  </p>
                </React.Fragment>
              ) }
            </div>

            <div className="w-full md:w-7/12 pt-12 md:p-6">
            {isLoggedIn &&
                    
                      <Button
                        variant="inline"
                        icon={['fas', 'download']}
                        iconPosition="right"
                        onClick={() => window.open(`/download/order/files/${props?.orderId}/visible`)}
                      >
                        <FormattedMessage {...messages.downloadAllFiles} />
                      </Button>
                  }
              <ol className="list-decimal list-inside text-md leading-loose" style={{ listStyle: 'decimal-leading-zero inside' }}>
                { files.map((file, i) => (
                  <li key={i} className={`rounded px-2 cursor-pointer hover:bg-gray-300 group ${index == i ? 'bg-gray-200 break-all' : 'truncate'}`} onClick={() => onPlayIndex(i)}>
                    { file.displayedName }
                    { (isLoggedIn || file?.taskType == "AU") &&
                      <Icon
                        onClick={(e) => {e.preventDefault(); window.open(`/download/task/file/${file.uid}`, '_blank')}}
                        className='ml-2 text-xs cursor-pointer text-primary'
                        icon='download'
                      />
                    }
                  </li>
                )) }
              </ol>
            
            </div>
          </div>
        </div>
      </main>

      <footer className="flex justify-center items-center py-6 text-white bg-tertiary space-x-8">
        <div className="w-1/4 flex space-x-4">
          { loading ? (
            <Icon icon="spinner-third" className="animate-spin" />
          ) : (
            <>
              <SeekBar
                currentTime={currentTime}
                duration={duration}
                isPlaying={isPlaying}
                pause={() => play(false)}
                play={() => play(true)}
                seek={seek}
              />

              <Timer
                currentTime={currentTime}
                duration={duration}
              />
            </>
          ) }
        </div>

        <div className="hidden md:block space-x-4"> 
          <span className="bg-white px-4 rounded text-tertiary">
            &rarr;
          </span>

          <FormattedMessage {...messages.next} tagName="span" />

          <span className="bg-white px-4 rounded text-tertiary">
            &larr;
          </span>

          <FormattedMessage {...messages.previous} tagName="span" />

          <span className="bg-white px-4 rounded text-tertiary">
            Space
          </span>

          <FormattedMessage {...messages.playPause} tagName="span" />
        </div>
      </footer>
    </div>
  )
}

export default PublicCustomerOrderPage
